import React, { useMemo, useState } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  Tooltip,
  Button,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { Typography } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridSortModel,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import CustomBox from "../CustomBox/CustomBox";
import { useTranslation } from "react-i18next";

export interface Column<T> {
  id: keyof T;
  label: string;
  align?: "left" | "center" | "right";
  format?: (value: any, row: T) => React.ReactNode;
  sortable?: boolean;
  flex?: number;
  valueFormatter?: (params: any) => string;
  renderCell?: (params: any) => React.ReactNode;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  page: number;
  rowsPerPage: number;
  totalRows: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSort?: (columnId: keyof T, order: "asc" | "desc") => void;
  orderBy?: keyof T;
  order?: "asc" | "desc";
  rowKey: keyof T | ((row: T) => string | number);
  onRowClick?: (row: T) => void;
  loading?: boolean;
  emptyMessage?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  searchKeys?: (keyof T)[];
  onSearchChange?: (searchTerm: string) => void;
  onFilterClick?: () => void;
  onAddButtonClick?: () => void;
  addButtonText?: string;
  checkboxSelection?: boolean;
  onSelectionModelChange?: (selection: GridRowSelectionModel) => void;
  selectionModel?: GridRowSelectionModel;
  onDeleteClick?: () => void;
  title?: string;
  description?: string;
}

function Table<T extends Record<string, any>>({
  columns,
  data,
  page,
  rowsPerPage,
  totalRows,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  orderBy,
  order = "asc",
  rowKey,
  onRowClick,
  loading = false,
  emptyMessage = "No data available",
  searchable = false,
  searchPlaceholder = "Search...",
  searchKeys,
  onSearchChange,
  onFilterClick,
  onAddButtonClick,
  addButtonText = "Add New",
  checkboxSelection = false,
  onSelectionModelChange,
  selectionModel,
  onDeleteClick,
  title,
  description,
}: TableProps<T>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchTerm, setSearchTerm] = useState("");
  const [sortModel, setSortModel] = useState<GridSortModel>(
    orderBy ? [{ field: orderBy as string, sort: order }] : [],
  );

  const getRowId = (row: T) => {
    return typeof rowKey === "function" ? rowKey(row) : String(row[rowKey]);
  };

  const filteredData = useMemo(() => {
    // If onSearchChange is provided, use server-side search (no client-side filtering)
    if (onSearchChange || !searchable || !searchTerm) return data;

    const searchTermLower = searchTerm.toLowerCase();
    return data.filter((row) => {
      const keysToSearch =
        searchKeys ||
        (Object.keys(row).filter(
          (key) => typeof row[key] === "string" || typeof row[key] === "number",
        ) as Array<keyof T>);

      return keysToSearch.some((key) => {
        const value = row[key];
        if (value === undefined || value === null) return false;
        return String(value).toLowerCase().includes(searchTermLower);
      });
    });
  }, [data, searchTerm, searchable, searchKeys, onSearchChange]);

  const displayedData = (searchable && !onSearchChange) ? filteredData : data;

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    if (onSort && newSortModel.length > 0) {
      const { field, sort } = newSortModel[0];
      onSort(field as keyof T, sort as "asc" | "desc");
    } else if (onSort) {
      onSort("" as keyof T, "asc");
    }
  };

  const gridColumns: GridColDef[] = useMemo(() => {
    return columns.map((column) => ({
      field: String(column.id),
      headerName: column.label,
      ...(isMobile
        ? { minWidth: 150, width: 150 }
        : { flex: column.flex || 1 }),
      sortable: column.sortable !== false,
      align: column.align,
      headerAlign: column.align,
      valueFormatter:
        column.valueFormatter ||
        ((params: any) => {
          if (!params || params.value === undefined || params.value === null)
            return "";
          return String(params.value);
        }),
      renderCell:
        column.renderCell ||
        ((params) => {
          const displayValue = column.format
            ? column.format(params.value, params.row)
            : params.value;

          const tooltipValue =
            params.value !== undefined && params.value !== null
              ? String(params.value)
              : "";

          return (
            <div
              style={{
                width: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={tooltipValue}
            >
              {displayValue}
            </div>
          );
        }),
    }));
  }, [columns, isMobile]);

  return (
    <CustomBox>
      {(title || description) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            {title && (
              <Typography variant="h6" sx={{ color: "white", fontWeight: 600 }}>
                {title}
              </Typography>
            )}
            {description && (
              <Typography
                variant="body2"
                sx={{ color: "rgba(255, 255, 255, 0.7)" }}
              >
                {description}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
          "& .MuiDataGrid-root": {
            border: "none",
            color: "white",
            backgroundColor: "transparent",
            "--DataGrid-rowBorderColor": "transparent",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "transparent",
              color: "white",
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "transparent",
              "&:focus, &:focus-within": {
                outline: "none !important",
              },
            },
            "& .MuiDataGrid-cell": {
              color: "white",
              backgroundColor: "transparent",
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              "&:focus, &:focus-within": {
                outline: "none !important",
              },
            },
            "& .MuiDataGrid-row": {
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.05)",
              },
              "&.Mui-selected": {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.12)",
                },
              },
            },
            "& .MuiTablePagination-root": {
              color: "white",
            },
            "& .MuiSvgIcon-root, & .MuiSelect-icon": {
              color: "white",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: "transparent",
              borderTop: "none",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            mb: 2,
            gap: 2,
            width: "100%",
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              justifyContent: "flex-start",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 1, sm: 0 },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {searchable && (
              <TextField
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => {
                  const newSearchTerm = e.target.value;
                  setSearchTerm(newSearchTerm);
                  if (onSearchChange) {
                    // Server-side search: call callback
                    onSearchChange(newSearchTerm);
                  } else {
                    // Client-side search: reset to first page
                    onPageChange(0);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "white" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: "100%" },
                  maxWidth: { xs: "none", sm: 300 },
                  background: "transparent",
                  borderRadius: "11px",

                  "& input": {
                    color: "white",
                    height: "40px",
                    padding: "0 12px",
                    boxSizing: "border-box",
                  },

                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    border: "1px solid #474747ff",
                    borderRadius: "11px",
                    "&:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline":
                    {
                      border: "red",
                    },
                  },
                }}
              />
            )}
            {onFilterClick && (
              <Button
                variant="contained"
                startIcon={<FilterIcon />}
                onClick={onFilterClick}
                sx={{
                  whiteSpace: "nowrap",
                  minWidth: "auto",
                  height: "40px",
                  color: "primary",
                  border: "1px solid #474747ff",
                  fontWeight: "600",
                  ml: { xs: 0, sm: 1 },
                  borderRadius: "11px",
                  width: { xs: "100%", sm: "auto" },
                  "&:hover": {
                    backgroundColor: "secondary",
                  },
                }}
              >
                {t("common.filters")}
              </Button>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "stretch", sm: "flex-end" },
              gap: 1,
              flexDirection: { xs: "column", sm: "row" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {checkboxSelection && (
              <Tooltip
                title="Zaznacz co najmniej jeden element"
                placement="top"
                arrow
              >
                <span style={{ display: "inline-block" }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={onDeleteClick}
                    disabled={true}
                    sx={{
                      minWidth: { xs: "100%", sm: "40px" },
                      width: { xs: "100%", sm: "40px" },
                      height: "40px",
                      padding: 0,
                      border: "1px solid #474747ff",
                      borderRadius: "11px",
                      "&:hover": {
                        backgroundColor: "secondary",
                      },
                    }}
                  >
                    <DeleteIcon />
                  </Button>
                </span>
              </Tooltip>
            )}
            {onAddButtonClick && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={onAddButtonClick}
                sx={{
                  whiteSpace: "nowrap",
                  height: "40px",
                  border: "1px solid #474747ff",
                  borderRadius: "11px",
                  fontWeight: "600",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {addButtonText}
              </Button>
            )}
          </Box>
        </Box>
        <Paper
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: isMobile ? "auto" : "hidden",
            overflowX: isMobile ? "auto" : "hidden",
            backgroundColor: "transparent",
            boxShadow: "none",
            width: "100%",
            maxWidth: "100%",
          }}
        >
          <Box
            style={{
              height: "100%",
              width: isMobile ? "max-content" : "100%",
              overflow: isMobile ? "visible" : "hidden",
              minWidth: isMobile ? "100%" : 0,
              maxWidth: isMobile ? "none" : "100%",
            }}
          >
            <DataGrid
              rows={displayedData}
              columns={gridColumns}
              getRowId={getRowId}
              loading={loading}
              paginationModel={{
                pageSize: rowsPerPage,
                page: page,
              }}
              onPaginationModelChange={(model) => {
                onPageChange(model.page);
                if (model.pageSize !== rowsPerPage) {
                  onRowsPerPageChange(model.pageSize);
                }
              }}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              rowCount={(searchable && !onSearchChange) ? filteredData.length : totalRows}
              paginationMode={(searchable && !onSearchChange) ? "client" : "server"}
              sortingMode={onSort ? "server" : "client"}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              disableRowSelectionOnClick
              disableColumnMenu={true}
              onRowClick={(params: { row: T }) => onRowClick?.(params.row as T)}
              sx={{
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus, ":
                {
                  outline: "none",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "transparent",
                  color: "white",
                  fontWeight: "bold",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                },
                "& .MuiDataGrid-row": {
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: "#353535",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.23)",
                    borderRadius: "11px",
                  },
                },
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                },
                cursor: onRowClick ? "pointer" : "default",
                minWidth: isMobile ? "max-content" : 0,
                maxWidth: isMobile ? "none" : "100%",
                width: isMobile ? "max-content" : "100%",
                overflow: isMobile ? "visible" : "hidden",
              }}
              checkboxSelection={checkboxSelection}
              onRowSelectionModelChange={onSelectionModelChange}
              rowSelectionModel={selectionModel}
              slots={{
                noRowsOverlay: () => (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    {emptyMessage}
                  </div>
                ),
                noResultsOverlay: () => (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    {emptyMessage}
                  </div>
                ),
              }}
              localeText={{
                paginationRowsPerPage: "",
                footerRowSelected: (count) => `Zaznaczonych: ${count}`,
              }}
            />
          </Box>
        </Paper>
      </Box>
    </CustomBox>
  );
}

export default Table;
